import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { isBiometricAvailable, isBiometricEnabled, tryBiometricLogin } from '../services/biometric';

const A = '#00D4AA';

export default function Login() {
  const { login, loginMfa, loginMetToken, forcedSetupStart, forcedSetupVerify, voltooiMfaSetup } = useAuth();
  const [email, setEmail] = useState('');
  const [wachtwoord, setWachtwoord] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [bioBeschikbaar, setBioBeschikbaar] = useState(false);
  const [bioEnabled, setBioEnabled] = useState(false);
  const [bioAutoGeprobeerd, setBioAutoGeprobeerd] = useState(false);
  const [bioOnthoud, setBioOnthoud] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [setupToken, setSetupToken] = useState(null);
  const [setupQr, setSetupQr] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [sessieToken, setSessieToken] = useState(null);

  useEffect(() => {
    (async () => {
      setBioBeschikbaar(await isBiometricAvailable());
      setBioEnabled(await isBiometricEnabled());
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const r = await login(email.trim(), wachtwoord, bioOnthoud);
      if (r && r.requiresMfa) { setMfaToken(r.mfaToken); }
      else if (r && r.requiresMfaSetup) {
        setSetupToken(r.setupToken);
        const st = await forcedSetupStart(r.setupToken);
        setSetupQr(st.qr); setSetupSecret(st.secret);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Inloggen mislukt. Controleer je gegevens.');
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await loginMfa(mfaToken, mfaCode.trim(), bioOnthoud);
    } catch (err) {
      setError(err.response?.data?.message || 'Onjuiste code.');
    } finally {
      setBusy(false);
    }
  };

  const submitSetup = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const r = await forcedSetupVerify(setupToken, setupCode.trim());
      setRecoveryCodes(r.recoveryCodes);
      setSessieToken(r.token);
    } catch (err) {
      setError(err.response?.data?.message || 'Onjuiste code, probeer opnieuw.');
    } finally {
      setBusy(false);
    }
  };

  const voltooiSetup = async () => {
    setBusy(true);
    try {
      await voltooiMfaSetup(sessieToken, bioOnthoud);
    } catch (err) {
      setError('Er ging iets mis bij het voltooien. Log opnieuw in.');
      setBusy(false);
    }
  };

  const bioLogin = async () => {
    setError(''); setBusy(true);
    try {
      const token = await tryBiometricLogin();
      if (!token) { setBusy(false); return; }
      await loginMetToken(token);
    } catch (err) {
      setError('Vingerafdruk-sessie verlopen. Log opnieuw in met je wachtwoord.');
      setBioEnabled(false);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (bioBeschikbaar && bioEnabled && !bioAutoGeprobeerd && !mfaToken && !setupToken) {
      setBioAutoGeprobeerd(true);
      if (window.__skipBioAuto) { window.__skipBioAuto = false; return; }
      setTimeout(() => bioLogin(), 400);
    }
    // eslint-disable-next-line
  }, [bioBeschikbaar, bioEnabled, bioAutoGeprobeerd, mfaToken, setupToken]);

  const inputStyle = { width: '100%', padding: '12px 14px', marginBottom: 16, borderRadius: 10, border: '1px solid #1f2937', background: '#0a0e1a', color: '#e2e8f0', boxSizing: 'border-box' };

  const renderSetup = () => {
    if (recoveryCodes) {
      return (
        <div>
          <p style={{ color: '#e2e8f0', fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Tweestapsverificatie ingeschakeld</p>
          <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 14 }}>Bewaar deze herstelcodes op een veilige plek. Je ziet ze slechts eenmalig. Met een herstelcode kun je inloggen als je je authenticator kwijt bent.</p>
          <div style={{ background: '#0a0e1a', border: '1px solid #1f2937', borderRadius: 10, padding: 14, marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {recoveryCodes.map((c) => (
              <div key={c} style={{ fontFamily: 'monospace', fontSize: 14, color: '#e2e8f0', textAlign: 'center', letterSpacing: 1 }}>{c}</div>
            ))}
          </div>
          {error && <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
          <button type="button" onClick={voltooiSetup} disabled={busy} style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: busy ? '#0f766e' : A, color: '#0a0e1a', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
            {busy ? 'Bezig\u2026' : 'Ik heb ze opgeslagen \u2014 doorgaan'}
          </button>
        </div>
      );
    }
    return (
      <form onSubmit={submitSetup}>
        <p style={{ color: '#e2e8f0', fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Tweestapsverificatie instellen</p>
        <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 14 }}>Voor jouw account is tweestapsverificatie verplicht. Scan de QR-code met een authenticator-app (bijv. Google of Microsoft Authenticator) en voer de getoonde code in.</p>
        {setupQr ? (
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <img src={setupQr} alt="QR-code" style={{ width: 180, height: 180, background: '#fff', borderRadius: 10, padding: 8 }} />
          </div>
        ) : (
          <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', marginBottom: 12 }}>QR-code laden...</div>
        )}
        {setupSecret && (
          <p style={{ color: '#64748b', fontSize: 12, textAlign: 'center', marginBottom: 14, wordBreak: 'break-all' }}>
            Of handmatig: <span style={{ fontFamily: 'monospace', color: '#94a3b8' }}>{setupSecret}</span>
          </p>
        )}
        <input type="text" inputMode="numeric" autoComplete="one-time-code" value={setupCode} onChange={(e) => setSetupCode(e.target.value)} placeholder="123456" autoFocus
          style={{ ...inputStyle, textAlign: 'center', fontSize: 20, letterSpacing: 4 }} />
        {error && <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
        <button type="submit" disabled={busy} style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: busy ? '#0f766e' : A, color: '#0a0e1a', fontWeight: 700, fontSize: 15 }}>
          {busy ? 'Bezig\u2026' : 'Activeren'}
        </button>
      </form>
    );
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: '#0a0e1a' }}>
      <div style={{ width: '100%', maxWidth: 380, background: '#111827', borderRadius: 16, padding: 28, boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>WOM<span style={{ color: A }}>sys</span></div>
          <div style={{ color: '#94a3b8', fontSize: 14, marginTop: 4 }}>Klantportaal · v2</div>
        </div>

        {setupToken ? renderSetup() : mfaToken ? (
          <form onSubmit={submitMfa}>
            <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 14 }}>Voer de 6-cijferige code uit je authenticator-app in (of een herstelcode).</p>
            <input type="text" inputMode="numeric" autoComplete="one-time-code" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder="123456" autoFocus
              style={{ ...inputStyle, textAlign: 'center', fontSize: 20, letterSpacing: 4 }} />
            {error && <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
            <button type="submit" disabled={busy} style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: busy ? '#0f766e' : A, color: '#0a0e1a', fontWeight: 700, fontSize: 15 }}>
              {busy ? 'Bezig\u2026' : 'Verifiëren'}
            </button>
            <button type="button" onClick={() => { setMfaToken(null); setMfaCode(''); setError(''); }} style={{ width: '100%', padding: 10, marginTop: 10, background: 'transparent', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer' }}>Terug</button>
          </form>
        ) : (
          <>
            <form onSubmit={submit}>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: 13, marginBottom: 6 }}>E-mailadres</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" style={inputStyle} />
              <label style={{ display: 'block', color: '#94a3b8', fontSize: 13, marginBottom: 6 }}>Wachtwoord</label>
              <input type="password" value={wachtwoord} onChange={(e) => setWachtwoord(e.target.value)} required autoComplete="current-password" style={inputStyle} />
              {bioBeschikbaar && !bioEnabled && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: 13, marginBottom: 16, cursor: 'pointer' }}>
                  <input type="checkbox" checked={bioOnthoud} onChange={(e) => setBioOnthoud(e.target.checked)} />
                  Vingerafdruk inschakelen voor de volgende keer
                </label>
              )}
              {error && <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
              <button type="submit" disabled={busy} style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: busy ? '#0f766e' : A, color: '#0a0e1a', fontWeight: 700, fontSize: 15 }}>
                {busy ? 'Bezig\u2026' : 'Inloggen'}
              </button>
            </form>
            {bioBeschikbaar && bioEnabled && (
              <button type="button" onClick={bioLogin} disabled={busy} style={{ width: '100%', padding: '13px', borderRadius: 10, border: '1px solid ' + A, background: 'transparent', color: A, fontWeight: 700, fontSize: 15, marginTop: 12, cursor: 'pointer' }}>
                {busy ? 'Bezig…' : 'Inloggen met vingerafdruk'}
              </button>
            )}
            <p style={{ color: '#64748b', fontSize: 12, textAlign: 'center', marginTop: 18, marginBottom: 0 }}>
              Eerste keer? Gebruik de link in je uitnodigingsmail om een wachtwoord in te stellen.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
